"use client"

import { useState, useEffect, useId } from "react"
import { createPortal } from "react-dom"
import { m, AnimatePresence } from "framer-motion"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import type { Availability } from "@/lib/availability"
import { useCountry } from "@/components/layout/country-provider"
import { buildFallback } from "@/lib/pricing"
import { ActionBadge } from "@/components/ui/action-badge"
import {
  CheckCircleOutlineIcon,
  ChevronDownIcon,
  CloseIcon,
  InfoContrastIcon,
  InfoForWhomIcon,
  InfoHealthAreasIcon,
  InfoIcon,
  InfoPreparationIcon,
  InfoRadiationIcon,
  InfoTimeIcon,
  PacemakerIcon,
  PersonHeartIcon,
} from "@/components/ui/icons"

/**
 * Collapsed-state row limit for the biomarkers grid. A "row" is either a
 * category title (full-width) or a pair of items (2-col cells). Anything
 * beyond this is hidden behind a "View more" toggle. Keep in sync with the
 * design at Figma 529:5861.
 */
const BIOMARKER_ROW_LIMIT = 10

/**
 * Truncate the biomarker category list so that the rendered grid has at
 * most `maxRows` rows (category title + ceil(items/2) item rows per
 * category). Only used when the user hasn't expanded the list.
 */
function truncateBiomarkersToRows(
  categories: { category: string; items: string[] }[],
  maxRows: number
): { category: string; items: string[] }[] {
  let rowsUsed = 0
  const result: { category: string; items: string[] }[] = []
  for (const cat of categories) {
    // Need at least title + 1 pair row to be worth showing the category.
    if (maxRows - rowsUsed < 2) break
    const pairsAvailable = maxRows - rowsUsed - 1
    const itemsFit = Math.min(cat.items.length, pairsAvailable * 2)
    if (itemsFit === 0) break
    result.push({ category: cat.category, items: cat.items.slice(0, itemsFit) })
    rowsUsed += 1 + Math.ceil(itemsFit / 2)
  }
  return result
}

export interface ProductInfoData {
  nameKey: string
  /** CMS package ID for deriving biomarker lists from CMS data at runtime */
  cmsPackageId?: string
  image?: string
  durationKey?: string
  radiationKey?: string
  contrastDyeKey?: string
  /** Stripe price-map key (core, advanced, blood, addon*) — display resolves
   *  live feed first, then the central fallback table in lib/pricing.ts. */
  priceKey?: string
  /** Static price — ONLY for products deliberately not in the Stripe map
   *  (Lp(a)/PSA/Cortisol). Stripe-mapped products use `priceKey`. */
  price?: string
  descKey: string
  benefitKeys?: string[]
  audienceKeys?: string[]
  whatYouGetKeys?: string[]
  scanAreaKeys?: string[]
  infoKeys?: {
    healthAreas?: string
    timeNeeded?: string
    radiation?: string
    contrastDye?: string
    preparation?: string
    forWhom?: string
  }
  /** Per-country-locale availability. Absent = available everywhere. */
  availability?: Availability
}

/** Biomarker data grouped by CMS category, ready for rendering */
export interface BiomarkerGroup {
  category: string
  items: string[]
}

/** Resolved data with translated strings for rendering and test access */
export interface ResolvedProductInfo {
  name: string
  image?: string
  duration?: string
  radiation?: string
  contrastDye?: string
  price?: string
  priceKey?: string
  description: string
  keyBenefits?: string[]
  whoIsItFor?: string[]
  whatYouGet?: string[]
  biomarkers?: { category: string; items: string[] }[]
  scanAreas?: string[]
  info?: {
    healthAreas?: string
    timeNeeded?: string
    radiation?: string
    contrastDye?: string
    preparation?: string
    forWhom?: string
  }
}

function resolveProductInfo(
  data: ProductInfoData,
  t: (key: string) => string,
  cmsBiomarkers?: BiomarkerGroup[]
): ResolvedProductInfo {
  return {
    name: t(data.nameKey),
    image: data.image,
    duration: data.durationKey ? t(data.durationKey) : undefined,
    radiation: data.radiationKey ? t(data.radiationKey) : undefined,
    contrastDye: data.contrastDyeKey ? t(data.contrastDyeKey) : undefined,
    price: data.price,
    priceKey: data.priceKey,
    description: t(data.descKey),
    keyBenefits: data.benefitKeys?.map((k) => t(k)),
    whoIsItFor: data.audienceKeys?.map((k) => t(k)),
    whatYouGet: data.whatYouGetKeys?.map((k) => t(k)),
    biomarkers: cmsBiomarkers,
    scanAreas: data.scanAreaKeys?.map((k) => t(k)),
    info: data.infoKeys
      ? {
          healthAreas: data.infoKeys.healthAreas
            ? t(data.infoKeys.healthAreas)
            : undefined,
          timeNeeded: data.infoKeys.timeNeeded
            ? t(data.infoKeys.timeNeeded)
            : undefined,
          radiation: data.infoKeys.radiation
            ? t(data.infoKeys.radiation)
            : undefined,
          contrastDye: data.infoKeys.contrastDye
            ? t(data.infoKeys.contrastDye)
            : undefined,
          preparation: data.infoKeys.preparation
            ? t(data.infoKeys.preparation)
            : undefined,
          forWhom: data.infoKeys.forWhom ? t(data.infoKeys.forWhom) : undefined,
        }
      : undefined,
  }
}

export function ProductInfoButton({
  data,
  biomarkerGroups,
  notAvailable = false,
  livePrice,
}: {
  data: ProductInfoData
  /** Pre-grouped biomarkers from CMS for this product */
  biomarkerGroups?: BiomarkerGroup[]
  /** Service is `not_available` in the current market (CMS-driven) — show the
   *  terracotta "Not available" pill in the modal header. */
  notAvailable?: boolean
  /** Live formatted full price (e.g. the live "320" figure) from the parent's Stripe feed.
   *  When present it overrides the static `data.price` from product-info.ts so
   *  the modal never drifts from the card it was opened from. */
  livePrice?: string
}) {
  const [open, setOpen] = useState(false)
  const [biomarkersExpanded, setBiomarkersExpanded] = useState(false)
  // Defer portal mount until after hydration — `document.body` isn't
  // available during SSR, and we want the modal to escape any
  // transformed ancestor (the wizard's framer-motion m.div otherwise
  // anchors `position: fixed` to itself, collapsing the modal around
  // the trigger).
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const descId = useId()
  const t = useTranslations("pricing")
  const ut = useTranslations("ui")
  // [TODO: confirm DE pricing] — `resolved.price` is a CHF figure. Hide the
  // price line on EUR markets until we have authoritative prices.
  const { code: country, currency } = useCountry()
  const showPrice = currency === "CHF"
  const resolved = resolveProductInfo(
    data,
    t as unknown as (key: string) => string,
    biomarkerGroups
  )
  // Live Stripe price (already formatted) wins; otherwise the central
  // fallback table resolves the product's priceKey, so no per-product price
  // literal exists outside lib/pricing.ts. `resolved.price` remains only for
  // products deliberately not in the Stripe map.
  const priceLabel =
    livePrice ??
    (resolved.priceKey
      ? buildFallback(country).fullPrices[resolved.priceKey]
      : undefined) ??
    resolved.price

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    // Measure the actual scrollbar width BEFORE locking, then compensate
    // body with padding-right so the page doesn't shift when the
    // document scrollbar disappears (Radix Dialog pattern). On platforms
    // with overlay scrollbars (macOS default) this resolves to 0px and is
    // a no-op — so we don't introduce shifts where none existed.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth
    const prevOverflow = document.body.style.overflow
    const prevPaddingRight = document.body.style.paddingRight
    document.body.style.overflow = "hidden"
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }
    window.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.style.paddingRight = prevPaddingRight
      window.removeEventListener("keydown", handler)
    }
  }, [open])

  // Reset biomarkers expansion when modal closes so it collapses again next
  // time the user opens it.
  useEffect(() => {
    if (!open) setBiomarkersExpanded(false)
  }, [open])

  const biomarkerTotalRows =
    resolved.biomarkers?.reduce(
      (sum, cat) => sum + 1 + Math.ceil(cat.items.length / 2),
      0
    ) ?? 0
  const biomarkerOverflow = biomarkerTotalRows > BIOMARKER_ROW_LIMIT
  const visibleBiomarkers =
    resolved.biomarkers && biomarkerOverflow && !biomarkersExpanded
      ? truncateBiomarkersToRows(resolved.biomarkers, BIOMARKER_ROW_LIMIT)
      : resolved.biomarkers

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setOpen(true)
        }}
        className="group/info -m-[9px] ml-0 inline-flex min-h-[40px] min-w-[40px] flex-shrink-0 cursor-pointer items-center justify-center rounded-full"
        aria-label="More info"
      >
        <InfoIcon size={22} />
      </button>
      {/* Portal the modal to <body> so it escapes (a) the card's stacking
          context — the Advanced card's `pill-magic-stroke` adds
          `isolation: isolate`, trapping the modal below siblings/navbar even
          at z-[100] — and (b) any transformed ancestor (the wizard's
          framer-motion m.div); `position: fixed` only anchors to the viewport
          when no ancestor has `transform`. `mounted` gates SSR/hydration. */}
      {mounted &&
        createPortal(
          /* safe-animate-presence: conditional-mount — child only renders when `open=true` */
          <AnimatePresence>
            {open && (
              <>
                {/* Overlay is also the scroll container — page-level scrolling */}
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-black/40 backdrop-blur-sm"
                  onClick={() => setOpen(false)}
                >
                  <div className="flex min-h-full items-start justify-center p-3 sm:items-center sm:p-6 sm:py-16">
                    <m.div
                      initial={{ opacity: 0, scale: 0.96, y: 16 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: 16 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      role="dialog"
                      aria-modal="true"
                      aria-label={resolved.name}
                      aria-describedby={descId}
                      onClick={(e) => e.stopPropagation()}
                      className="relative w-full overflow-hidden bg-white"
                      style={{
                        borderRadius: 24,
                        maxWidth: 860,
                        padding: "36px 24px 24px",
                      }}
                    >
                      <span id={descId} className="sr-only">
                        Press Escape to close
                      </span>
                      {/* Header row — Figma 505:8048 */}
                      <div className="flex items-start gap-4">
                        {resolved.image && (
                          <div
                            className="flex-shrink-0 overflow-hidden rounded-[16px]"
                            style={{ width: 92, height: 92 }}
                          >
                            <Image
                              src={resolved.image}
                              alt=""
                              width={92}
                              height={92}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="type-headline-md text-neutral-900">
                              {resolved.name}
                            </h2>
                            {notAvailable && (
                              <ActionBadge
                                label={ut("notAvailable")}
                                color="neutral"
                                className="bg-neutral-100"
                              />
                            )}
                          </div>
                          <p className="type-body-lg text-neutral-500">
                            {[
                              resolved.duration &&
                                `${t("piLabelTimeNeeded")}: ${resolved.duration}`,
                              resolved.radiation,
                              resolved.contrastDye,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          {showPrice && priceLabel && (
                            <p className="type-body-lg text-neutral-900">
                              CHF{" "}
                              <span className="font-bold">
                                {priceLabel.replace(/^CHF\s*/, "")}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="type-body-lg mt-6 text-neutral-900">
                        {resolved.description}
                      </p>

                      {/* Key benefits + Who is it for — side by side tiles */}
                      {(resolved.keyBenefits || resolved.whoIsItFor) && (
                        <div className="mt-6 grid grid-cols-1 gap-2 md:grid-cols-2">
                          {resolved.keyBenefits && (
                            <div className="bg-background flex items-start gap-4 rounded-[16px] p-4">
                              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-neutral-900">
                                <PacemakerIcon size={24} />
                              </span>
                              <div className="flex min-w-0 flex-1 flex-col gap-4 py-1">
                                <p className="type-title-md text-neutral-900">
                                  {t("piSectionKeyBenefits")}
                                </p>
                                <ul className="flex flex-col gap-1.5">
                                  {resolved.keyBenefits.map((b) => (
                                    <li
                                      key={b}
                                      className="type-body-md flex gap-2.5 text-neutral-900"
                                    >
                                      <span
                                        aria-hidden="true"
                                        className="mt-[9px] h-1 w-1 flex-shrink-0 rounded-full bg-neutral-400"
                                      />
                                      <span>{b}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                          {resolved.whoIsItFor && (
                            <div className="bg-background flex items-start gap-4 rounded-[16px] p-4">
                              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-neutral-900">
                                <PersonHeartIcon size={24} />
                              </span>
                              <div className="flex min-w-0 flex-1 flex-col gap-4 py-1">
                                <p className="type-title-md text-neutral-900">
                                  {t("piSectionWhoIsItFor")}
                                </p>
                                <ul className="flex flex-col gap-1.5">
                                  {resolved.whoIsItFor.map((w) => (
                                    <li
                                      key={w}
                                      className="type-body-md flex gap-2.5 text-neutral-900"
                                    >
                                      <span
                                        aria-hidden="true"
                                        className="mt-[9px] h-1 w-1 flex-shrink-0 rounded-full bg-neutral-400"
                                      />
                                      <span>{w}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {resolved.whatYouGet && (
                        <div className="mt-6">
                          <p className="type-title-md py-2 text-neutral-900">
                            {t("piSectionWhatYouGet")}
                          </p>
                          <ul className="border-t border-neutral-200 pt-1">
                            {resolved.whatYouGet.map((item) => (
                              <li
                                key={item}
                                className="type-body-lg flex h-11 items-center gap-2 text-neutral-900"
                              >
                                <CheckCircleOutlineIcon
                                  size={20}
                                  className="text-light-green-500 flex-shrink-0"
                                />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {visibleBiomarkers && visibleBiomarkers.length > 0 && (
                        <div className="mt-6">
                          <p className="type-title-md py-2 text-neutral-900">
                            {t("piSectionBiomarkers")}
                          </p>
                          <div className="border-t border-neutral-200 pt-1">
                            {visibleBiomarkers.map((cat, catIndex) => (
                              <div
                                key={cat.category}
                                className={`grid grid-cols-2 gap-x-6 ${catIndex > 0 ? "mt-1 border-t border-neutral-200 pt-1" : ""}`}
                              >
                                <div className="type-title-sm col-span-2 flex h-10 items-center text-neutral-900">
                                  {cat.category}
                                </div>
                                {cat.items.map((item) => (
                                  <div
                                    key={item}
                                    className="type-body-md flex h-10 items-center text-neutral-900"
                                  >
                                    {item}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                          {biomarkerOverflow && (
                            <button
                              type="button"
                              onClick={() => setBiomarkersExpanded((v) => !v)}
                              className="mt-6 flex w-full cursor-pointer items-center gap-3 p-1"
                              aria-expanded={biomarkersExpanded}
                            >
                              <span
                                aria-hidden="true"
                                className="h-px flex-1 bg-neutral-200"
                              />
                              <span className="flex items-center gap-1">
                                <span className="type-title-sm text-neutral-900">
                                  {biomarkersExpanded
                                    ? t("piViewLess")
                                    : t("piViewMore")}
                                </span>
                                <span className="bg-background flex size-6 items-center justify-center rounded-full text-neutral-900">
                                  <ChevronDownIcon
                                    width={14}
                                    height={14}
                                    className={`transition-transform duration-200 ${biomarkersExpanded ? "rotate-180" : ""}`}
                                  />
                                </span>
                              </span>
                              <span
                                aria-hidden="true"
                                className="h-px flex-1 bg-neutral-200"
                              />
                            </button>
                          )}
                        </div>
                      )}

                      {resolved.info && (
                        <div className="mt-6">
                          <p className="type-title-md py-2 text-neutral-900">
                            {t("piSectionInformation")}
                          </p>
                          <div className="bg-background rounded-[16px] p-4">
                            {[
                              {
                                icon: <InfoHealthAreasIcon size={18} />,
                                label: t("piLabelHealthAreas"),
                                value: resolved.info.healthAreas,
                              },
                              {
                                icon: <InfoTimeIcon size={18} />,
                                label: t("piLabelTimeNeeded"),
                                value: resolved.info.timeNeeded,
                              },
                              {
                                icon: <InfoRadiationIcon size={18} />,
                                label: t("piLabelRadiation"),
                                value: resolved.info.radiation,
                              },
                              {
                                icon: <InfoContrastIcon size={18} />,
                                label: t("piLabelContrastDye"),
                                value: resolved.info.contrastDye,
                              },
                              {
                                icon: <InfoPreparationIcon size={18} />,
                                label: t("piLabelPreparation"),
                                value: resolved.info.preparation,
                              },
                              {
                                icon: <InfoForWhomIcon size={18} />,
                                label: t("piLabelForWhom"),
                                value: resolved.info.forWhom,
                              },
                            ]
                              .filter((row) => row.value)
                              .map((row) => (
                                /* Label/value sit side by side from sm up. On
                                   a phone the pair doesn't fit (AHD-10495):
                                   the label was `flex-shrink-0` and the value
                                   kept a flex item's default `min-width:
                                   auto`, so a German row like
                                   "Gesundheitsbereiche" + "Brustgewebe" blew
                                   past the panel's padding. Below sm the
                                   value now takes its own full-width line
                                   (`w-full` forces the wrap), which leaves
                                   both strings enough room to wrap at word
                                   boundaries instead of being chopped.
                                   It also flips to `text-left` there: the
                                   right alignment only reads as alignment
                                   while the value sits opposite the label,
                                   and the `pl-8` (icon 20px + gap 12px)
                                   lines it up under the label instead. */
                                <div
                                  key={row.label}
                                  className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-0.5 py-1 pr-2"
                                >
                                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-neutral-900">
                                    {row.icon}
                                  </span>
                                  <span className="type-body-lg min-w-0 text-neutral-900">
                                    {row.label}
                                  </span>
                                  <span className="type-body-lg ml-auto min-w-0 text-right text-neutral-600 max-sm:w-full max-sm:pl-8 max-sm:text-left">
                                    {row.value}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {resolved.scanAreas && (
                        <div className="mt-6">
                          <p className="type-title-md py-2 text-neutral-900">
                            {t("piSectionAreasCovered")}
                          </p>
                          <div className="flex flex-wrap gap-2 pt-2">
                            {resolved.scanAreas.map((area) => (
                              <span
                                key={area}
                                className="type-body-md rounded-full bg-neutral-100 px-4 py-1.5 text-neutral-900"
                              >
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </m.div>
                  </div>
                </m.div>
                {/* Close button — fixed to viewport, outside the modal, always reachable */}
                <m.button
                  type="button"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => setOpen(false)}
                  className="fixed top-4 right-6 z-[110] flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70 sm:top-5 sm:right-7"
                  aria-label="Close"
                  autoFocus
                >
                  <CloseIcon width={22} height={22} />
                </m.button>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  )
}
